import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import {
  ArchiveBoxIcon,
  ClockIcon,
  CurrencyPoundIcon,
} from "@heroicons/react/24/outline";

import { IVehicleCategoryStockTableRow } from "../../data/useVehicleCategoryStockTable";
import PriceFormat from "../../utils/priceFormat";
import LoadingPulse from "../shared/LoadingPulse";

interface Props {
  rows: IVehicleCategoryStockTableRow[];
  isLoading?: boolean;
}

interface Row {
  id: string;
  name: string;
  stock: number;
  avgPrice: number;
  value: number;
  avgDaysInStock: number;
}

export default function PartsInStockTable({ rows: sourceRows, isLoading = false }: Props) {
  const { t } = useTranslation("jobs");
  const { rows, totalStock, totalValue, avgPrice, weightedAvgDaysInStock } = useMemo(() => {
    const rows: Row[] = sourceRows
      .map((row) => ({
        id: row.categoryId,
        name: row.categoryName,
        stock: row.internalStock ?? 0,
        avgPrice: row.averagePrice ?? 0,
        value: row.totalValue ?? 0,
        avgDaysInStock: row.averageDaysInStock ?? 0,
      }))
      .filter((row) => row.stock > 0)
      .sort((a, b) => b.stock - a.stock || b.value - a.value);

    const totalStock = rows.reduce((sum, r) => sum + r.stock, 0);
    const totalValue = rows.reduce((sum, r) => sum + r.value, 0);
    const avgPrice = totalStock > 0 ? totalValue / totalStock : 0;
    const weightedAvgDaysInStock =
      totalStock > 0
        ? rows.reduce((sum, r) => sum + (r.avgDaysInStock * r.stock), 0) / totalStock
        : 0;

    return { rows, totalStock, totalValue, avgPrice, weightedAvgDaysInStock };
  }, [sourceRows]);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-start justify-between px-6 pt-5 pb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {t("Parts in stock — by category")}
        </h3>
      </div>

      <div className="grid grid-cols-1 gap-3 px-6 pb-4 sm:grid-cols-3">
        <StatCard
          colour="cyan"
          icon={<ArchiveBoxIcon className="h-5 w-5" />}
          value={totalStock.toLocaleString()}
          label={t("In stock")}
          sub={rows.length === 1 ? t("{{count}} category", { count: rows.length }) : t("{{count}} categories", { count: rows.length })}
        />
        <StatCard
          colour="blue"
          icon={<CurrencyPoundIcon className="h-5 w-5" />}
          value={PriceFormat(totalValue)}
          label={t("Stock value")}
          sub={t("avg {{price}}", { price: PriceFormat(avgPrice) })}
        />
        <StatCard
          colour="violet"
          icon={<ClockIcon className="h-5 w-5" />}
          value={weightedAvgDaysInStock > 0 ? t("{{days}}d", { days: weightedAvgDaysInStock.toFixed(0) }) : "—"}
          label={t("Avg age")}
          sub={t("weighted by stock")}
        />
      </div>

      <div className="border-t border-gray-200 px-6 py-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">
          {t("Stocked categories")}
        </div>
        <div className="text-xs text-gray-500">{t("Sorted by stock count")}</div>
      </div>

      <div className="max-h-[420px] overflow-y-auto">
        <table className="min-w-full">
          <thead className="sticky top-0 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-600">
            <tr>
              <th className="px-6 py-2 text-left">{t("Category")}</th>
              <th className="px-3 py-2 text-center">{t("Stock")}</th>
              <th className="px-3 py-2 text-right">{t("Avg Days")}</th>
              <th className="px-3 py-2 text-right">{t("Avg Price")}</th>
              <th className="px-6 py-2 text-right">{t("Value")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-sm">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-6 text-center text-gray-500 italic">
                  {isLoading ? <LoadingPulse width="w-full" /> : t("No stocked categories")}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-6 py-2.5 text-gray-900">{row.name}</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className="inline-flex min-w-[2rem] items-center justify-center rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-sm font-semibold text-amber-700">
                      {row.stock}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right text-amber-700">
                    {row.avgDaysInStock > 0 ? row.avgDaysInStock.toFixed(0) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right text-gray-700">
                    {row.avgPrice > 0 ? PriceFormat(row.avgPrice) : "—"}
                  </td>
                  <td className="px-6 py-2.5 text-right font-semibold text-emerald-700">
                    {row.value > 0 ? PriceFormat(row.value) : "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const COLOUR_STYLES: Record<string, { bg: string; ring: string; icon: string; value: string }> = {
  cyan: {
    bg: "bg-cyan-50",
    ring: "ring-cyan-100",
    icon: "text-cyan-600",
    value: "text-cyan-700",
  },
  blue: {
    bg: "bg-blue-50",
    ring: "ring-blue-100",
    icon: "text-blue-600",
    value: "text-blue-700",
  },
  violet: {
    bg: "bg-violet-50",
    ring: "ring-violet-100",
    icon: "text-violet-600",
    value: "text-violet-700",
  },
};

function StatCard({
  colour,
  icon,
  value,
  label,
  sub,
}: {
  colour: keyof typeof COLOUR_STYLES;
  icon: React.ReactNode;
  value: string;
  label: string;
  sub?: string;
}) {
  const styles = COLOUR_STYLES[colour];
  return (
    <div className={`flex items-center gap-3 rounded-xl ${styles.bg} px-4 py-3 ring-1 ${styles.ring}`}>
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg bg-white/70 ${styles.icon}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <div className={`text-2xl font-bold leading-tight ${styles.value}`}>{value}</div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">{label}</div>
        {sub && <div className="text-xs text-gray-500">{sub}</div>}
      </div>
    </div>
  );
}
